/**
 * Paywall - subscription screen with products, features, and purchase flow
 * V2 of ScreenBasicPaywall - uses PaywallConfig with flat structure
 *
 * Media alignment:
 * - topAlignment controls whether media starts at screen top or below the header.
 * - bottomAlignment controls whether intro content is below media or overlays media.
 * - legacy bodyAppearance.heroAnchor remains a fallback for existing V2 Paywalls.
 */

import React, { memo, useCallback, useMemo, useContext } from 'react';
import type { CSSProperties } from 'react';
import type { StepNode, ProductOffer, GradientOverlay, LocalizedLabel } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import { resolveLabel } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import { createButtonClickEvent, createListItemSelectEvent } from '../../../utils/eventEmitter';
import { hexToRgba } from '../../../utils/styleMapper';

import { Text } from '../../shared/Text';
import { BulletList } from '../../shared/BulletList';
import { Loader } from '../../shared/Loader';
import { Background } from '../../shared/Background';
import { Media } from '../../shared/Media';
import { PaywallHeader } from '../../shared/PaywallHeader';
import { PaywallFooter } from '../../shared/PaywallFooter';
import { ProductCatalog } from '../../shared/ProductCatalog';
import type { LegalLinksEventContext } from '../../shared/LegalLinks';
import styles from './Paywall.module.css';
import { PurchaseSlotContext } from '@modules/screen-view-v2/purchaseSlot';

type PaywallBodyAppearanceOverlay = {
  overlay?: GradientOverlay | null;
  overlayKind?: string | null;
};

type PaywallMediaTopAlignment = 'top' | 'navigationBar';
type PaywallMediaBottomAlignment = 'contentTop' | 'contentBottom';

const normalizeGradientColors = (colors: unknown): string[] => {
  if (Array.isArray(colors)) {
    return colors.filter((color): color is string => typeof color === 'string' && color.length > 0);
  }

  if (colors && typeof colors === 'object') {
    return Object.values(colors as Record<string, unknown>).filter(
      (color): color is string => typeof color === 'string' && color.length > 0
    );
  }

  return [];
};

const clampPercentage = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback;
  }

  return Math.min(100, Math.max(0, value));
};

const toTransparentColor = (color: string): string => {
  if (/^#[0-9a-f]{3}([0-9a-f]{3})?([0-9a-f]{2})?$/i.test(color)) {
    return hexToRgba(color, 0);
  }

  return 'rgba(0, 0, 0, 0)';
};

export function mapPaywallMediaGradientToCSS(gradient?: GradientOverlay | null): CSSProperties | undefined {
  if (!gradient) return undefined;

  const colors = normalizeGradientColors((gradient as { colors?: unknown }).colors);
  const bottomColor = colors[0];
  if (!bottomColor) return undefined;

  const heightPercent = clampPercentage((gradient as { heightPercentage?: unknown }).heightPercentage, 50);

  return {
    backgroundImage: `linear-gradient(0deg, ${bottomColor} 0%, ${toTransparentColor(bottomColor)} ${heightPercent}%)`,
  };
}

export function mapPaywallBodyAppearanceGradientToCSS(
  bodyAppearance?: PaywallBodyAppearanceOverlay | null
): CSSProperties | undefined {
  if (!bodyAppearance?.overlay) return undefined;
  if (bodyAppearance.overlayKind && bodyAppearance.overlayKind !== 'gradient') return undefined;

  return mapPaywallMediaGradientToCSS(bodyAppearance.overlay);
}

export function resolvePaywallMediaTopAlignment(
  topAlignment?: string | null
): PaywallMediaTopAlignment {
  return topAlignment === 'navigationBar' ? 'navigationBar' : 'top';
}

export function resolvePaywallMediaBottomAlignment(
  bottomAlignment?: string | null,
  legacyHeroAnchor?: string | null
): PaywallMediaBottomAlignment {
  if (bottomAlignment === 'contentTop' || bottomAlignment === 'contentBottom') {
    return bottomAlignment;
  }

  return legacyHeroAnchor === 'HeaderBottom' ? 'contentBottom' : 'contentTop';
}

export interface PaywallProps {
  step: StepNode;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
  /** Optional loading state for purchase processing */
  isLoading?: boolean;
  /** Currently selected subscription index (controlled) */
  selectedSubscriptionIndex?: number | null;
  /** Callback when subscription selection changes */
  onSubscriptionChange?: (index: number | null) => void;
  /** Callback when footer link is clicked (privacy, terms, restore) */
  onLinkClick?: (context: LegalLinksEventContext) => void;
  /** Per-product template variables (productId → variables) */
  itemVariablesMap?: Record<string, TemplateVariables>;
}

export const Paywall = memo(function Paywall({
  step,
  locale,
  variables,
  resolvedScreenValues,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
  isLoading = false,
  selectedSubscriptionIndex,
  onSubscriptionChange,
  onLinkClick,
  itemVariablesMap,
}: PaywallProps): React.ReactElement {
  const { content } = step;
  const paywall = content?.paywall;

  // New media alignment fields take priority. heroAnchor is legacy fallback only.
  const heroAnchor = paywall?.bodyAppearance?.heroAnchor;
  const mediaTopAlignment = resolvePaywallMediaTopAlignment(content?.featuredMedia?.appearance?.topAlignment);
  const mediaBottomAlignment = resolvePaywallMediaBottomAlignment(
    content?.featuredMedia?.appearance?.bottomAlignment,
    heroAnchor
  );
  const isMediaScreenTop = mediaTopAlignment === 'top';
  const isMediaBehindIntroContent = mediaBottomAlignment === 'contentBottom';
  const hasBulletItems = Boolean(content?.options?.items?.length);

  // Build paywall-specific media fade from V2 bodyAppearance.
  const getGradientStyle = () => {
    return mapPaywallBodyAppearanceGradientToCSS(paywall?.bodyAppearance as PaywallBodyAppearanceOverlay | undefined);
  };

  // Handle subscription selection
  const handleSubscriptionSelect = useCallback((item: ProductOffer, index: number) => {
    onSubscriptionChange?.(index);

    if (onAction) {
      onAction(createListItemSelectEvent(
        `subscription-item-${index}`,
        step,
        item.productId || `product-${index}`
      ));
    }
  }, [onSubscriptionChange, step, onAction]);

  // Handle restore purchases
  const handleRestoreClick = useCallback(() => {
    if (onEvent) {
      onEvent(createButtonClickEvent('restore-purchases', step, 'restore-button'));
    }
  }, [onEvent, step]);

  // --- Footer fallbacks from the selected product (ported from V1 ScreenBasicPaywall) ---
  // Selected product resolution: explicit index → product with isSelected → items[0].
  // The defensive default avoids an empty-button flash before useV2Purchase's auto-select
  // effect runs, and makes the fallback visible in the editor preview.
  const hasPurchaseSlot = useContext(PurchaseSlotContext) !== null;
  const productItems = paywall?.products?.items;
  const selectedProduct = useMemo<ProductOffer | undefined>(() => {
    const list = productItems ?? [];
    if (selectedSubscriptionIndex != null && list[selectedSubscriptionIndex]) {
      return list[selectedSubscriptionIndex];
    }
    const selectedIdx = list.findIndex((item) => item.isSelected);
    return selectedIdx >= 0 ? list[selectedIdx] : list[0];
  }, [productItems, selectedSubscriptionIndex]);

  // Fallback 1: purchase button text empty for locale → inject selected product's purchaseLabel.
  // Keeps the button's own appearance/layout; skips icon buttons; never overrides existing text.
  const purchaseActionWithFallback = useMemo(() => {
    const purchaseAction = paywall?.purchaseAction;
    if (!purchaseAction || purchaseAction.kind === 'icon' || !selectedProduct?.purchaseLabel) {
      return purchaseAction;
    }
    const labelContent = purchaseAction.content as LocalizedLabel | undefined;
    if (resolveLabel(labelContent, locale)) {
      return purchaseAction; // explicit text present — do not override
    }
    return {
      ...purchaseAction,
      content: {
        ...(labelContent as LocalizedLabel),
        translations: selectedProduct.purchaseLabel.translations,
        templateParams: selectedProduct.purchaseLabel.templateParams,
        kind: selectedProduct.purchaseLabel.kind,
      },
    };
  }, [paywall?.purchaseAction, selectedProduct, locale]);

  // Fallback 2: auto-renew label exists but empty for locale → inject selected product's
  // descriptionLabel. Only fills an existing shell — never synthesizes one when absent.
  const autoRenewLabelWithFallback = useMemo(() => {
    const autoRenewLabel = paywall?.autoRenewLabel;
    if (!autoRenewLabel || !selectedProduct?.descriptionLabel || resolveLabel(autoRenewLabel, locale)) {
      return autoRenewLabel;
    }
    return {
      ...autoRenewLabel,
      translations: selectedProduct.descriptionLabel.translations,
      templateParams: selectedProduct.descriptionLabel.templateParams,
      kind: selectedProduct.descriptionLabel.kind,
    };
  }, [paywall?.autoRenewLabel, selectedProduct, locale]);

  // Merge the selected product's price variables so @price/@interval inside the injected
  // (or authored) footer labels resolve — mirrors V1's footerVariables.
  const footerVariables = useMemo(() => {
    const productId = selectedProduct?.productId;
    const itemVars = productId ? itemVariablesMap?.[productId] : undefined;
    return itemVars ? { ...variables, ...itemVars } : variables;
  }, [variables, itemVariablesMap, selectedProduct]);

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  // D-T9b-13: only the live runtime scrolls. Editor previews and canvas thumbnails keep
  // the unscrolled composition they are measured and screenshotted with.
  const isLiveRuntime = previewMode === 'live';

  // A sticky header needs something to be sticky OVER. When the canvas is a solid colour
  // we can pin it and the content passes behind it cleanly; for gradient/image canvases we
  // leave the header in flow rather than smear text over text.
  const canvasBackground = content?.canvas?.background as
    | { kind?: string; content?: { color?: string } }
    | undefined;
  const solidCanvasColor = canvasBackground?.kind === 'solid' ? canvasBackground.content?.color : undefined;
  const headerPinned = isLiveRuntime && Boolean(solidCanvasColor);

  const headerWrapperClassName = [
    styles.stickyHeader,
    headerPinned ? styles.stickyHeaderPinned : undefined,
    isLiveRuntime && isMediaScreenTop ? styles.stickyHeaderTopInset : undefined,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${isMediaScreenTop ? ` ${styles.noPaddingTop}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      data-testid="view-paywall"
    >
      {/* Background layer */}
      <Background
        backdrop={content?.canvas?.background}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      {/* Paywall header. In the live runtime it sits ABOVE the scrolling body instead of
          floating over it, so the decline path and Restore are always reachable and can
          never paint over the first product card (D-T9b-13). */}
      <div
        className={headerWrapperClassName}
        style={headerPinned ? { backgroundColor: solidCanvasColor } : undefined}
      >
        <PaywallHeader
          closeButton={paywall?.closeButton}
          restoreLabel={paywall?.restoreLabel}
          headerAppearance={paywall?.headerAppearance}
          step={step}
          locale={locale}
          onAction={onAction}
          onRestoreClick={handleRestoreClick}
          editPath={ep ? `${ep}.paywall` : undefined}
          absolute={isMediaScreenTop && !isLiveRuntime}
        />
      </div>

      {/* Main content area */}
      <div
        className={`${styles.content}${isLiveRuntime ? ` ${styles.contentScrollable}` : ''}`}
        data-testid="view-paywall-body"
      >
        {isMediaBehindIntroContent ? (
          <>
            {/* Media behind titles+bullets, subscriptions outside */}
            <div className={styles.headerBottomContainer}>
              {/* Media layer - absolutely positioned backdrop */}
              {content?.featuredMedia && (
                <div className={styles.mediaBackdrop}>
                  <Media
                    media={content.featuredMedia}
                    locale={locale}
                    editPath={ep ? `${ep}.featuredMedia` : undefined}
                    previewMode={previewMode}
                    fillContainer
                  />
                  {getGradientStyle() && (
                    <div
                      className={styles.mediaGradient}
                      style={getGradientStyle()}
                      data-testid="paywall-media-gradient"
                    />
                  )}
                </div>
              )}

              {/* Overlay content - titles + bullets on top of media */}
              <div className={`${styles.overlayContent} ${styles.xPaddings}`}>
                <div className={styles.header}>
                  <Text
                    label={content?.copy?.title}
                    locale={locale}
                    variables={variables}
                    resolvedScreenValues={resolvedScreenValues}
                    editPath={ep ? `${ep}.copy.title` : undefined}
                  />
                  <Text
                    label={content?.copy?.subtitle}
                    locale={locale}
                    variables={variables}
                    resolvedScreenValues={resolvedScreenValues}
                    editPath={ep ? `${ep}.copy.subtitle` : undefined}
                  />
                </div>
                {hasBulletItems && (
                  <div className={styles.listContainer}>
                    <BulletList
                      options={content?.options}
                      locale={locale}
                      editPath={ep ? `${ep}.options` : undefined}
                      previewMode={previewMode}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Subscriptions OUTSIDE - never behind media */}
            {paywall?.products && (
              <div className={`${styles.subscriptionContainer} ${styles.xPaddings}`}>
                <ProductCatalog
                  catalog={paywall.products}
                  locale={locale}
                  variables={variables}
                  itemVariablesMap={itemVariablesMap}
                  selectedIndex={selectedSubscriptionIndex}
                  onSelect={handleSubscriptionSelect}
                  editPath={ep ? `${ep}.paywall.products` : undefined}
                  previewMode={previewMode}
                />
              </div>
            )}
          </>
        ) : (
          /* Media above content, scrollable wrapper */
          <>
            {content?.featuredMedia && (
              <div className={styles.mediaContainer}>
                <Media
                  media={content.featuredMedia}
                  locale={locale}
                  editPath={ep ? `${ep}.featuredMedia` : undefined}
                  previewMode={previewMode}
                  fillContainer={isMediaScreenTop}
                />
                {getGradientStyle() && (
                  <div
                    className={styles.mediaGradient}
                    style={getGradientStyle()}
                    data-testid="paywall-media-gradient"
                  />
                )}
              </div>
            )}

            <div
              className={`${styles.mainContentWrapper} ${styles.xPaddings}`}
            >
              <div className={styles.header}>
                <Text
                  label={content?.copy?.title}
                  locale={locale}
                  variables={variables}
                  resolvedScreenValues={resolvedScreenValues}
                  editPath={ep ? `${ep}.copy.title` : undefined}
                />
                <Text
                  label={content?.copy?.subtitle}
                  locale={locale}
                  variables={variables}
                  resolvedScreenValues={resolvedScreenValues}
                  editPath={ep ? `${ep}.copy.subtitle` : undefined}
                />
              </div>

              {hasBulletItems && (
                <div className={styles.listContainer}>
                  <BulletList
                    options={content?.options}
                    locale={locale}
                    editPath={ep ? `${ep}.options` : undefined}
                    previewMode={previewMode}
                  />
                </div>
              )}

              {paywall?.products && (
                <div className={styles.subscriptionContainer}>
                  <ProductCatalog
                    catalog={paywall.products}
                    locale={locale}
                    variables={variables}
                    itemVariablesMap={itemVariablesMap}
                    selectedIndex={selectedSubscriptionIndex}
                    onSelect={handleSubscriptionSelect}
                    editPath={ep ? `${ep}.paywall.products` : undefined}
                    previewMode={previewMode}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer with purchase button, auto-renew label, and legal links */}
      <PaywallFooter
        purchaseAction={purchaseActionWithFallback}
        selectedProductId={selectedProduct?.productId}
        autoRenewLabel={autoRenewLabelWithFallback}
        legalLinks={paywall?.legalLinks}
        footerAppearance={paywall?.footerAppearance}
        step={step}
        locale={locale}
        variables={footerVariables}
        onAction={onAction}
        onLinkClick={onLinkClick}
        isLoading={isLoading}
        className={styles.footer}
        editPath={ep ? `${ep}.paywall` : undefined}
      />

      {/* Without an authored loader, the footer button already shows pending state. */}
      {isLoading && !hasPurchaseSlot && paywall?.loader && (
        <div className={styles.loaderOverlay} data-testid="paywall-loader">
          <Loader loader={paywall?.loader} />
        </div>
      )}
    </div>
  );
});

Paywall.displayName = 'Paywall';
